<template>
  <div>
    <div v-if="!isCallback" class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('shared.back') }}</span>
    </div>

    <!-- Callback: user re-entered this page via a different browser than the
         one that requested the link, so we have no email cached. Ask for it
         so signInWithEmailLink can complete. -->
    <template v-if="isCallback && needsEmail">
      <h2 class="auth-heading">{{ $t('on_boarding.magic_link_page.confirm_your_email') }}</h2>
      <p class="auth-sub">
        {{ $t('on_boarding.magic_link_page.it_looks_like_you_opened_the') }}
      </p>

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="completeWithEmail">
        <div class="pro-field mb-4">
          <label class="pro-field__label" for="magicLinkConfirmEmail">{{ $t('shared.email') }}</label>
          <input
            id="magicLinkConfirmEmail"
            v-model.trim="email"
            type="email"
            class="form-control form-control-lg pro-field__input"
            placeholder="example@email.com"
            autocomplete="email"
            required
          >
        </div>

        <button
          type="submit"
          class="btn pro-btn pro-btn--accent w-100"
          :disabled="isLoading"
        >
          <template v-if="isLoading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span>{{ $t('on_boarding.magic_link_page.signing_you_in') }}</span>
          </template>
          <template v-else>
            <span class="material-icons">login</span>
            <span>{{ $t('on_boarding.magic_link_page.finish_sign_in') }}</span>
          </template>
        </button>
      </form>
    </template>

    <!-- Callback: the link couldn't be verified (expired / already used /
         tampered). Offer a fresh start. -->
    <template v-else-if="isCallback && errorMessage">
      <div class="sent-state text-center">
        <span class="material-icons sent-icon" style="color: #ff6b6b;">error_outline</span>
        <h2 class="auth-heading mt-3 mb-2">{{ $t('on_boarding.magic_link_page.we_couldn_t_sign_you_in') }}</h2>
        <p class="auth-sub">{{ errorMessage }}</p>
        <a href="/login" class="btn pro-btn pro-btn--secondary w-100 mt-3">
          <span class="material-icons">refresh</span>
          <span>{{ $t('on_boarding.magic_link_page.request_a_new_link') }}</span>
        </a>
      </div>
    </template>

    <!-- Callback: verifying the link / signing the user in. -->
    <template v-else-if="isCallback">
      <div class="sent-state text-center">
        <span class="material-icons sent-icon">auto_awesome</span>
        <h2 class="auth-heading mt-3 mb-2">{{ $t('on_boarding.magic_link_page.signing_you_in_2') }}</h2>
        <p class="auth-sub">{{ $t('on_boarding.magic_link_page.hang_tight_while_we_verify_your') }}</p>
      </div>
    </template>

    <!-- Default request flow: ask for email, send the link. -->
    <template v-else-if="!sent">
      <h2 class="auth-heading">{{ $t('on_boarding.magic_link_page.sign_in_with_a_magic_link') }}</h2>
      <p class="auth-sub">
        {{ $t('on_boarding.magic_link_page.enter_your_email_and_we_ll') }}
      </p>

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="requestLink">
        <div class="pro-field mb-4">
          <label class="pro-field__label" for="magicLinkEmail">{{ $t('shared.email') }}</label>
          <input
            id="magicLinkEmail"
            v-model.trim="email"
            type="email"
            class="form-control form-control-lg pro-field__input"
            placeholder="example@email.com"
            autocomplete="email"
            required
          >
        </div>

        <button
          type="submit"
          class="btn pro-btn pro-btn--accent w-100"
          :disabled="isLoading"
        >
          <template v-if="isLoading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span>{{ $t('on_boarding.magic_link_page.sending_link') }}</span>
          </template>
          <template v-else>
            <span class="material-icons">auto_awesome</span>
            <span>{{ $t('on_boarding.magic_link_page.send_sign_in_link') }}</span>
          </template>
        </button>
      </form>
    </template>

    <!-- Request flow confirmation. -->
    <template v-else>
      <div class="sent-state text-center">
        <span class="material-icons sent-icon">mark_email_read</span>
        <h2 class="auth-heading mt-3 mb-2">{{ $t('on_boarding.magic_link_page.check_your_inbox') }}</h2>
        <i18n-t keypath="on_boarding.magic_link_page.we_ve_sent_a_sign_in_link" tag="p" class="auth-sub" scope="global">
          <template #email><strong class="sent-email">{{ submittedEmail }}</strong></template>
        </i18n-t>
        <i18n-t keypath="on_boarding.magic_link_page.didn_t_get_it_check_your_spam" tag="p" class="text-muted small mt-3" scope="global">
          <template #link><a href="#" class="link-yellow" @click.prevent="reset">{{ $t('on_boarding.magic_link_page.try_a_different_email') }}</a></template>
        </i18n-t>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { auth } from '../../../utils/firebase/auth';
import { isSignInWithEmailLink } from 'firebase/auth';

export default defineComponent({
  name: 'OnBoardingMagicLinkPage',
  data() {
    return {
      email: '',
      submittedEmail: '',
      sent: false,
      // Computed once on mount: this controls which branch the template
      // renders. We don't recompute it because the URL is cleaned up after a
      // successful sign-in (we don't want the verifying state to flip to the
      // request form just because the query params got stripped).
      isCallback: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    needsEmail(): boolean {
      return this.$store.state.onBoarding.magicLinkNeedsEmail;
    },
  },
  created() {
    this.isCallback = isSignInWithEmailLink(auth, window.location.href);
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
    async requestLink() {
      await this.$store.dispatch('onBoarding/requestMagicLink', this.email.trim());
      if (!this.errorMessage) {
        this.submittedEmail = this.email.trim();
        this.sent = true;
      }
    },
    async completeWithEmail() {
      await this.$store.dispatch('onBoarding/completeMagicLinkSignIn', this.email.trim());
    },
    reset() {
      this.sent = false;
      this.email = '';
      this.submittedEmail = '';
      this.$store.commit('onBoarding/setError', null);
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
    this.$store.commit('onBoarding/setMagicLinkNeedsEmail', false);
  },
});
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #a1a4ad;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.5rem;
}
.auth-sub {
  color: #a1a4ad;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1.25rem;
}

.sent-state {
  padding: 0.5rem 0 1.5rem;
}
.sent-icon {
  font-size: 3.5rem;
  color: var(--yellow, #fddf00);
  filter: drop-shadow(0 8px 24px rgba(253, 223, 0, 0.25));
}
.sent-email {
  color: #fff;
  font-weight: 700;
}
.link-yellow {
  color: var(--yellow, #fddf00);
}
.link-yellow:hover {
  text-decoration: underline;
}
</style>
